import type * as mapboxgl from 'mapbox-gl';

import { MAP_EDITOR_ICONS as ICONS } from './map-editor-icons';
import type { MapListEntry } from './map-polygon-list';
import type { WhispFeatureMark } from './whisp-feature-findings';

type WhispFilter = 'all' | WhispFeatureMark;

const WHISP_MARK_COLORS: Record<WhispFeatureMark, string> = { risk: '#e53935', ok: '#2e7d32' };

export interface MapListLabels {
  search: string;
  empty: string;
  whisp?: Record<WhispFilter, string>;
}

export interface MapListApi {
  areasLabel: string;
  editLabel: string;
  listLabels: MapListLabels;
  getState: () => { isDrawing: boolean; isEditing: boolean; hasPolygon: boolean };
  getPolygonList: () => MapListEntry[];
  focusZoomEdit: (id: string) => void;
  focusZoomEditAndShowCoords: (id: string) => void;
}

export interface MapEditApi {
  readOnly?: false;
  startPolygon: () => void;
  startCircle: () => void;
  addPointLabel: string;
  drawAreaLabel: string;
  deleteLabel: string;
  clearPolygon: () => void;
  removeAllLabel: string;
  deletePolygonById: (id: string) => void;
}

export type MapToolsApi = MapListApi & (MapEditApi | { readOnly: true });

export class MapToolsControl implements mapboxgl.IControl {
  private _map: mapboxgl.Map | null = null;
  private _container!: HTMLElement;

  private btnDraw!: HTMLButtonElement;
  private btnCircle!: HTMLButtonElement;
  private polyListWrap!: HTMLElement;
  private polyList!: HTMLElement;
  private rowById = new Map<string, HTMLElement>();
  private search!: HTMLInputElement;
  private filters!: HTMLElement;
  private whispFilter?: HTMLSelectElement;
  private items: MapListEntry[] = [];
  private filtered: typeof this.items = [];
  private lastSelected = '';
  private readonly rowHeight = 36;
  private listResizeObserver?: ResizeObserver;

  constructor(private api: MapToolsApi) {}

  private get hoverBackground(): string {
    return this.api.readOnly ? 'rgba(0, 0, 0, 0.06)' : '#f7f7f7';
  }

  private svg(name: string): string {
    return ICONS[name] ?? `<svg viewBox="0 0 24 24" width="20" height="20"><circle cx="12" cy="12" r="10"/></svg>`;
  }

  private iconBtn({
    svgName,
    title,
    onClick,
    small = false,
    aria = title,
  }: {
    svgName: string;
    title: string;
    onClick: () => void;
    small?: boolean;
    aria?: string;
  }) {
    const b = document.createElement('button');
    b.type = 'button';
    b.title = title;
    if (svgName === 'draw') b.dataset['testid'] = 'map-start-polygon-button';
    if (svgName === 'point') b.dataset['testid'] = 'map-add-point-button';
    b.setAttribute('aria-label', aria);
    b.className = 'custom-tools-ctrl__btn' + (small ? ' custom-tools-ctrl__btn--sm' : '');
    b.innerHTML = this.svg(svgName);
    b.style.cssText = `width:30px;height:30px;padding:5px;border-radius:6px;color:${svgName === 'point' ? '#7c3aed' : svgName === 'draw' ? '#16834a' : svgName === 'delete' ? '#b42318' : '#476873'};`;
    b.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onClick();
    });
    return b;
  }

  onAdd(map: mapboxgl.Map) {
    this._map = map;

    const root = document.createElement('div');
    root.className = 'mapboxgl-ctrl mapboxgl-ctrl-group custom-tools-ctrl';
    root.style.maxWidth = '350px';
    root.style.minWidth = '150px';
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.maxHeight = '90vh';

    const headerRow = document.createElement('div');
    headerRow.style.display = 'flex';
    headerRow.style.alignItems = 'center';
    headerRow.style.justifyContent = 'space-between';
    headerRow.style.padding = '6px';
    headerRow.style.borderRadius = '5px';
    headerRow.style.background = '#fff';
    headerRow.style.borderBottom = '1px solid #e5e5e5';

    const listHeader = document.createElement('div');
    listHeader.textContent = this.api.areasLabel;
    listHeader.style.fontWeight = '600';
    listHeader.style.fontSize = '12px';

    headerRow.appendChild(listHeader);
    if (!this.api.readOnly) headerRow.appendChild(this.editButtons(this.api));

    const listWrap = document.createElement('div');
    listWrap.style.overflow = 'auto';
    listWrap.style.borderRadius = '6px';
    listWrap.style.display = 'none';
    listWrap.style.background = '#fff';
    listWrap.style.maxHeight = '240px';

    const ul = document.createElement('div');
    ul.style.display = 'flex';
    ul.style.flexDirection = 'column';
    ul.style.padding = '4px';
    ul.style.gap = '4px';

    listWrap.appendChild(ul);

    root.appendChild(headerRow);
    const filters = (this.filters = document.createElement('div'));
    filters.style.cssText = 'display:flex;gap:4px;padding:6px;background:white;';
    this.search = document.createElement('input');
    this.search.type = 'search';
    this.search.placeholder = this.api.listLabels.search;
    this.search.setAttribute('aria-label', this.api.listLabels.search);
    this.search.style.cssText =
      'min-width:0;width:130px;flex:1;padding:5px;border:1px solid #ccc;border-radius:4px;font:12px system-ui;';
    const filterChanged = () => {
      this.polyListWrap.scrollTop = 0;
      this.filterItems();
      this.renderVisibleRows();
    };
    this.search.addEventListener('input', filterChanged);
    filters.append(this.search);
    root.appendChild(filters);
    root.appendChild(listWrap);
    if (this.api.readOnly) makeTranslucent(root, [headerRow, filters, listWrap], [this.search]);

    this.polyListWrap = listWrap;
    this.polyList = ul;
    ul.style.cssText = 'display:block;padding:0 4px;position:relative;';
    listWrap.addEventListener('scroll', () => this.renderVisibleRows());

    this._container = root;
    this.refresh();
    if (typeof ResizeObserver !== 'undefined') {
      this.listResizeObserver = new ResizeObserver(() => this.renderVisibleRows());
      this.listResizeObserver.observe(listWrap);
    }
    return this._container;
  }

  private editButtons(edit: MapEditApi): HTMLElement {
    this.btnCircle = this.iconBtn({
      svgName: 'point',
      title: edit.addPointLabel,
      onClick: () => edit.startCircle(),
    });

    this.btnDraw = this.iconBtn({
      svgName: 'draw',
      title: edit.drawAreaLabel,
      onClick: () => edit.startPolygon(),
    });

    const rightBtns = document.createElement('div');
    rightBtns.style.display = 'flex';
    rightBtns.style.gap = '4px';
    rightBtns.appendChild(this.btnCircle);
    rightBtns.appendChild(this.btnDraw);
    const clearAll = this.iconBtn({
      svgName: 'clearAll',
      title: edit.removeAllLabel,
      onClick: () => edit.clearPolygon(),
    });
    clearAll.style.color = '#b42318';
    clearAll.style.marginLeft = '4px';
    rightBtns.appendChild(clearAll);
    return rightBtns;
  }

  syncListHeightToMap(map: mapboxgl.Map) {
    try {
      const mapH = (map.getContainer() as HTMLElement).clientHeight;
      const headerH = Array.from(this._container.children)
        .slice(0, 2)
        .reduce((sum, child) => sum + (child as HTMLElement).offsetHeight, 0);
      const extra = 16;
      const max = Math.max(120, mapH - headerH - extra);
      this.polyListWrap.style.maxHeight = `${max}px`;
      this.renderVisibleRows();
    } catch {}
  }

  scrollToPolygon(id: string) {
    let index = this.filtered.findIndex((item) => item.id === id);
    if (index < 0 && this.items.some((item) => item.id === id)) {
      this.search.value = '';
      if (this.whispFilter) this.whispFilter.value = 'all';
      this.filterItems();
      index = this.filtered.findIndex((item) => item.id === id);
    }
    if (index < 0) return;
    const top = index * this.rowHeight;
    const height = this.polyListWrap.clientHeight || 240;
    if (top < this.polyListWrap.scrollTop || top + this.rowHeight > this.polyListWrap.scrollTop + height)
      this.polyListWrap.scrollTop = Math.max(0, top - height / 2);
    this.renderVisibleRows();
  }

  private renderPolygonList() {
    this.items = this.api.getPolygonList();
    const s = this.api.getState();

    this.polyListWrap.style.display = !s.isDrawing && this.items.length ? 'block' : 'none';
    this.syncWhispFilter();
    this.filterItems();
    this.renderVisibleRows();
    const selected = this.items.find((item) => item.selected)?.id ?? '';
    if (selected && selected !== this.lastSelected) this.scrollToPolygon(selected);
    this.lastSelected = selected;
  }

  private filterItems() {
    const query = this.search.value.trim().toLowerCase();
    const whisp = (this.whispFilter?.isConnected ? this.whispFilter.value : 'all') as WhispFilter;
    this.filtered = this.items.filter(
      (item) => item.label.toLowerCase().includes(query) && (whisp === 'all' || item.whisp === whisp)
    );
  }

  private syncWhispFilter() {
    const labels = this.api.listLabels.whisp;
    const wanted = !!labels && this.items.some((item) => item.whisp);
    if (!wanted) {
      this.whispFilter?.remove();
      return;
    }
    this.whispFilter ??= this.createWhispFilter(labels);
    if (!this.whispFilter.isConnected) this.filters.append(this.whispFilter);
  }

  private createWhispFilter(labels: Record<WhispFilter, string>): HTMLSelectElement {
    const select = document.createElement('select');
    select.dataset['testid'] = 'map-list-whisp-filter';
    select.setAttribute('aria-label', labels.all);
    select.style.cssText = 'flex:0 0 auto;max-width:130px;padding:4px;border:1px solid #ccc;border-radius:4px;font:12px system-ui;';
    for (const value of ['all', 'risk', 'ok'] as WhispFilter[]) select.add(new Option(labels[value], value));
    select.addEventListener('change', () => {
      this.polyListWrap.scrollTop = 0;
      this.filterItems();
      this.renderVisibleRows();
    });
    return select;
  }

  private whispMark(mark: WhispFeatureMark): HTMLElement {
    const badge = document.createElement('span');
    badge.dataset['testid'] = 'map-list-whisp';
    badge.dataset['whisp'] = mark;
    badge.title = this.api.listLabels.whisp?.[mark] ?? '';
    badge.setAttribute('aria-label', badge.title);
    badge.innerHTML = this.svg('whisp');
    badge.style.cssText = `display:inline-flex;flex:0 0 auto;width:16px;height:16px;margin-right:4px;color:${WHISP_MARK_COLORS[mark]};`;
    return badge;
  }

  private renderVisibleRows() {
    const fragment = document.createDocumentFragment();
    const height = this.polyListWrap.clientHeight || 240;
    const scrollTop = Math.min(
      this.polyListWrap.scrollTop,
      Math.max(0, this.filtered.length * this.rowHeight - height)
    );
    this.rowById.clear();
    if (!this.filtered.length) {
      const empty = document.createElement('div');
      empty.textContent = this.api.listLabels.empty;
      empty.style.cssText = 'padding:8px;font-size:12px;';
      this.polyList.replaceChildren(empty);
      return;
    }
    const start = Math.max(0, Math.floor(scrollTop / this.rowHeight) - 5);
    const end = Math.min(
      this.filtered.length,
      start + Math.ceil((this.polyListWrap.clientHeight || 240) / this.rowHeight) + 10
    );
    const spacer = (height: number) => {
      const element = document.createElement('div');
      element.style.height = `${height}px`;
      fragment.appendChild(element);
    };
    spacer(start * this.rowHeight);
    for (const item of this.filtered.slice(start, end)) {
      const row = document.createElement('div');
      row.dataset['featureId'] = item.id;
      row.style.height = `${this.rowHeight}px`;
      row.style.boxSizing = 'border-box';
      row.tabIndex = 0;
      row.setAttribute('role', 'button');
      row.setAttribute('aria-label', item.label);
      row.addEventListener('keydown', (event) => {
        if (event.target === row && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          this.api.focusZoomEdit(item.id);
        }
      });
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.justifyContent = 'space-between';
      row.style.paddingLeft = '4px';
      row.style.borderRadius = '4px';
      row.style.cursor = 'pointer';
      row.style.userSelect = 'none';
      row.style.border = '1px solid transparent';

      if (item.selected) {
        row.style.background = '#e8f5e9';
        row.style.borderColor = '#a5d6a7';
        if (item.hasError) row.style.boxShadow = 'inset 0 0 0 1px #f1998e';
      } else if (item.hasError) {
        row.style.background = '#fdecea';
        row.style.borderColor = '#f5c2c7';
      } else {
        row.addEventListener('mouseenter', () => (row.style.background = this.hoverBackground));
        row.addEventListener('mouseleave', () => (row.style.background = 'transparent'));
      }

      const label = document.createElement('div');
      label.textContent = item.label;
      label.style.fontSize = '11px';
      label.style.flex = '1 1 auto';
      label.style.cssText += 'min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
      label.title = item.label;

      const actions = document.createElement('div');
      actions.style.display = 'flex';
      actions.style.flex = '0 0 auto';

      const coordsBtn = this.iconBtn({
        svgName: this.api.readOnly ? 'info' : 'coords',
        title: this.api.editLabel,
        small: true,
        onClick: () => this.api.focusZoomEditAndShowCoords(item.id),
      });
      coordsBtn.style.minWidth = '24px';

      if (item.whisp) row.appendChild(this.whispMark(item.whisp));
      row.appendChild(label);
      actions.appendChild(coordsBtn);
      if (!this.api.readOnly) actions.appendChild(this.deleteButton(this.api, item.id));
      row.appendChild(actions);

      row.addEventListener('click', (ev) => {
        ev.preventDefault();
        this.api.focusZoomEdit(item.id);
      });

      fragment.appendChild(row);
      this.rowById.set(item.id, row);
    }
    spacer((this.filtered.length - end) * this.rowHeight);
    this.polyList.replaceChildren(fragment);
    this.polyListWrap.scrollTop = scrollTop;
  }

  private deleteButton(edit: MapEditApi, id: string): HTMLButtonElement {
    const delBtn = this.iconBtn({
      svgName: 'delete',
      title: edit.deleteLabel,
      small: true,
      onClick: () => edit.deletePolygonById(id),
    });
    delBtn.style.minWidth = '24px';
    return delBtn;
  }

  onRemove() {
    this.listResizeObserver?.disconnect();
    this.listResizeObserver = undefined;
    this._container?.parentNode?.removeChild(this._container);
    this._map = null;
  }

  refresh() {
    if (!this._container) return;
    this.renderPolygonList();
  }
}

function makeTranslucent(root: HTMLElement, sections: HTMLElement[], inputs: HTMLElement[]): void {
  root.style.background = 'rgba(255, 255, 255, 0.75)';
  for (const section of sections) {
    section.style.background = 'transparent';
    section.style.borderBottomColor = 'rgba(0, 0, 0, 0.08)';
  }
  for (const input of inputs) {
    input.style.background = 'rgba(255, 255, 255, 0.5)';
    input.style.borderColor = 'rgba(0, 0, 0, 0.15)';
  }
}
